import * as React from 'react';
import Segmented from 'antd/lib/segmented';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Divider from 'antd/lib/divider';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAppsDemoModeEnabled } from '@/selectors/selectors';
import {
    LayoutOutlined,
    TableOutlined,
    InsertRowRightOutlined,
    InsertRowBelowOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import CardListIcon from '@/components/icons/CardListIcon';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import {
    PageViewType,
    TabularLayoutType,
    PageViewContext,
    CombinedLayoutValue,
    PAGE_VIEW_LABELS,
} from '@/types/summaryView';
export { PageViewContext };
import { FiltersPopover } from './FiltersPopover';
import { DestinationSelector } from '@/components/appHome/DestinationSelector';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { parseListParam, getCombinedLayoutValue, handleCombinedLayoutChange } from '@/helpers/summaryViewHelpers';
import CheckableTag from 'antd/lib/tag/CheckableTag';
import { ClockCircleOutlined, RocketOutlined, CloseOutlined } from '@ant-design/icons';
import Flex from 'antd/lib/flex';

interface OverviewTableHeaderProps {
    context?: PageViewContext;
    summary: SummaryViewContextValue;
    onTrackFilterApply?: (metrics: string[], versions: string[]) => void;
}

export const OverviewTableHeader: React.FC<OverviewTableHeaderProps> = ({
    context = PageViewContext.AppHome,
    summary,
    onTrackFilterApply,
}) => {
    const location = useLocation();
    const {
        preferences,
        updatePreferences,
        availableDestinations,
        availableMetrics,
        displayedVersions,
        handleFilterApply,
        handleDestinationChange,
        handleKeywordsChange,
        handlePageViewChange,
        handleTabularLayoutChange,
        getStoredPreferences,
        loading: { isLoading },
    } = summary;

    const {
        pageView,
        tabularLayout,
        selectedMetricKeys,
        selectedKeywords,
        showLive,
        showNext,
        selectedDestinationIds,
    } = preferences;
    const [filtersVisible, setFiltersVisible] = React.useState(false);
    const isViewingAppsInDemoMode = useSelector(selectIsAppsDemoModeEnabled);
    const isTabularView = pageView === PageViewType.Tabular;
    const showKeywordFilter = isTabularView && !isViewingAppsInDemoMode;
    const showDestinationSelector =
        availableDestinations.length > 1 && (context === PageViewContext.AppHome || isTabularView);

    const defaultFilters = React.useMemo(() => {
        const stored = getStoredPreferences();
        const urlParams = new URLSearchParams(location.search);

        const metricsFromStorage = stored.selectedMetrics;
        const metricsFromUrl = parseListParam(urlParams.get('metrics'));
        const defaultMetrics = metricsFromStorage?.length ? metricsFromStorage : metricsFromUrl;

        return { defaultMetrics };
    }, [getStoredPreferences, location.search]);

    const labels = PAGE_VIEW_LABELS[context];
    const combinedLayoutValue = getCombinedLayoutValue(pageView, tabularLayout);
    const isAppHome = context === PageViewContext.AppHome;

    const createToggleHandler = (flagKey: 'showLive' | 'showNext') => () => {
        const currentValue = flagKey === 'showLive' ? showLive : showNext;
        updatePreferences({ [flagKey]: !currentValue });
    };

    const handleToggleLive = createToggleHandler('showLive');
    const handleToggleNext = createToggleHandler('showNext');

    const renderAppHomeLayoutToggle = () => (
        <Segmented
            value={combinedLayoutValue}
            onChange={(value) => handleCombinedLayoutChange(value as CombinedLayoutValue, updatePreferences)}
            style={{ backgroundColor: antGrey.grey3 }}
            options={[
                {
                    value: 'summary' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Summary">
                            <span>
                                <CardListIcon size={14} />
                            </span>
                        </Tooltip>
                    ),
                },
                {
                    value: 'standard' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Standard table">
                            <LayoutOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
                {
                    value: 'compact' as CombinedLayoutValue,
                    icon: (
                        <Tooltip title="Compact table">
                            <TableOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
            ]}
        />
    );

    const renderOrgOverviewTabularLayoutToggle = () => (
        <Segmented
            value={tabularLayout}
            onChange={(value) => handleTabularLayoutChange(value as TabularLayoutType)}
            style={{ backgroundColor: antGrey.grey3 }}
            options={[
                {
                    value: TabularLayoutType.Standard,
                    icon: (
                        <Tooltip title="Standard table">
                            <LayoutOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
                {
                    value: TabularLayoutType.Compact,
                    icon: (
                        <Tooltip title="Compact table">
                            <TableOutlined style={{ color: antGrey.grey7 }} />
                        </Tooltip>
                    ),
                },
            ]}
        />
    );

    const renderFiltersButton = () => {
        const attributeCount = selectedMetricKeys.length;
        const isCompactLayout = tabularLayout === TabularLayoutType.Compact;
        const filtersLabel = isCompactLayout ? 'Rows' : 'Columns';
        return (
            <Popover
                overlayClassName="filters-overlay"
                destroyTooltipOnHide
                content={
                    <FiltersPopover
                        isVisible={filtersVisible}
                        label={filtersLabel}
                        onApply={(metrics, versions) => {
                            setFiltersVisible(false);
                            handleFilterApply(metrics, versions);
                        }}
                        onTrack={onTrackFilterApply}
                        currentMetrics={selectedMetricKeys}
                        currentVersions={displayedVersions}
                        availableMetrics={availableMetrics}
                        isSubmitting={isLoading}
                        defaultMetrics={defaultFilters.defaultMetrics}
                    />
                }
                trigger="click"
                open={filtersVisible}
                placement="bottomRight"
                onOpenChange={setFiltersVisible}
            >
                <Button
                    icon={isCompactLayout ? <InsertRowBelowOutlined /> : <InsertRowRightOutlined />}
                    loading={isLoading}
                    disabled={isLoading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                    {filtersLabel}
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 22,
                            height: 22,
                            borderRadius: 11,
                            fontSize: 12,
                            color: runwayColors.black,
                            backgroundColor: antGrey.grey4,
                        }}
                    >
                        {attributeCount}
                    </span>
                </Button>
            </Popover>
        );
    };

    const renderReleaseToggles = () => (
        <Flex gap={8}>
            <CheckableTag
                checked={showLive}
                onChange={handleToggleLive}
                style={{
                    margin: 0,
                    color: showLive ? runwayColors.white : runwayColors.black,
                    borderColor: showLive ? runwayColors.primary : antGrey.grey5,
                    borderStyle: 'solid',
                    backgroundColor: showLive ? runwayColors.primary : runwayColors.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <RocketOutlined
                    style={{
                        color: showLive ? runwayColors.white : antGrey.grey6,
                        marginRight: 5,
                    }}
                />
                Live release
                {showLive && <CloseOutlined style={{ color: runwayColors.white, marginLeft: 10 }} />}
            </CheckableTag>
            <CheckableTag
                checked={showNext}
                onChange={handleToggleNext}
                style={{
                    margin: 0,
                    color: showNext ? runwayColors.white : runwayColors.black,
                    borderColor: showNext ? runwayColors.primary : antGrey.grey5,
                    borderStyle: 'solid',
                    backgroundColor: showNext ? runwayColors.primary : runwayColors.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <ClockCircleOutlined
                    style={{
                        color: showNext ? runwayColors.white : antGrey.grey6,
                        marginRight: 5,
                    }}
                />
                Next release
                {showNext && <CloseOutlined style={{ color: runwayColors.white, marginLeft: 10 }} />}
            </CheckableTag>
        </Flex>
    );

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                position: 'sticky',
                top: isAppHome ? -36 : 0,
                zIndex: 10,
                backgroundColor: runwayColors.white,
                marginBottom: 16,
                paddingBottom: 8,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {!isAppHome && (
                    <Segmented
                        value={pageView}
                        onChange={(value) => handlePageViewChange(value as PageViewType)}
                        style={{ padding: 3 }}
                        options={[
                            {
                                label: (
                                    <span
                                        style={{
                                            padding: '10px 0',
                                            display: 'inline-block',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            width: 120,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {labels.default}
                                    </span>
                                ),
                                value: PageViewType.Default,
                            },
                            {
                                label: (
                                    <span
                                        style={{
                                            padding: '10px 0',
                                            display: 'inline-block',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            width: 120,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {labels.timeline}
                                    </span>
                                ),
                                value: PageViewType.Timeline,
                            },
                            {
                                label: (
                                    <span
                                        style={{
                                            padding: '10px 0',
                                            display: 'inline-block',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            width: 120,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {labels.tabular}
                                    </span>
                                ),
                                value: PageViewType.Tabular,
                            },
                        ]}
                    />
                )}
                {showDestinationSelector && (
                    <DestinationSelector
                        destinations={availableDestinations}
                        selectedDestinationIds={selectedDestinationIds}
                        onSelectionChange={handleDestinationChange}
                        isLoading={isLoading}
                        requiresConfirmation
                        disabled={isLoading}
                    />
                )}
                {isTabularView && isAppHome && renderReleaseToggles()}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {showKeywordFilter && (
                    <Select
                        mode="tags"
                        open={false}
                        aria-label="Filter by keyword"
                        suffixIcon={<SearchOutlined style={{ color: antGrey.grey7 }} />}
                        tokenSeparators={[',']}
                        value={selectedKeywords}
                        onChange={handleKeywordsChange}
                        placeholder="Keywords (e.g. completed, 2.3.2)"
                        style={{ minWidth: 320, maxWidth: 420 }}
                    />
                )}
                {showKeywordFilter && <Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />}
                {isTabularView && renderFiltersButton()}
                {isAppHome ? renderAppHomeLayoutToggle() : isTabularView && renderOrgOverviewTabularLayoutToggle()}
            </div>
        </div>
    );
};
